/**
 * @deprecated Use `GrowthChart` instead. This is still currently in use in 9 places. Do not delete.
 */

// Third-party
import React, { Component } from 'react';
import moment from 'moment';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';

// Components
import { withErrorBoundary } from '../../../errors';
import { Input, DateRangeSelect, EditableInput, DateQuickRangeOneWay } from '../../../inputs';
import { DisclaimerTooltip } from '../../../tooltips';
import HypotheticalReturnDisclaimer from '../../../disclaimers/comparison/HypotheticalReturnDisclaimer';
import GrowthLegend from '../../growth-chart/GrowthLegend';
import { GenericEmptyState } from '../../../misc';
import { InstrumentCustomDisclaimer, BenchmarksDisclaimer, ShortHistoryPortfoliosDisclaimer } from '../../../disclaimers/comparison/index';

// Utils
import { formatMessage, formatNumber, FormattedMessage, LOCALE } from '../../../../../utils/i18n';
import { REBALANCING_PERIODS, RebalanceFrequency } from '../../../../../utils/portfolio/utils';
import { getGraphPerformanceDisclaimer } from '../../../../../utils/disclaimers/charts';
import { ROLLING_INTERVALS } from '../../../../../utils/portfolios';
import dom from '../../../../../utils/dom';
import {
  ChartItem,
  mergeChartItems,
  getExpandedPortfolioItems,
  getRollingIntervalFromDates,
  LABEL_FORMATTERS,
  monthsToPeriodFromValues,
} from '../../../../../utils/charts';
import { COMPARISON_ITEM_TYPES, CHART_STARTING_CAPITAL, TIME_PERIODS, PERIODS_VALUE_CI, PERIODS_VALUE_INC } from '../../../../../utils/constants';
import { DISCLAIMER_TAG } from '../../../../../utils/disclaimers/constants';
import { getGrowthChartOptions } from '../../growth-chart/utils/chart-options';

// Types
import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import LocalCurrency from '@/types/local-currency';
import ComparisonItem from '@/types/comparison-item';
import GrowthChartOptions from '@/types/growth-chart-options';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Locale from '@/types/locale';
import AppRegion from '@/types/app-region';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import { getMaxEndDate } from '@/utils/dates';
import { periodToMonths } from '@/utils/time';
import User from '@/types/user';
import GrowthAnnualizedROILegend from '../../growth-chart/GrowthAnnualizedROILegend';
import getSeriesDataImpl from '@/utils/comparison/get-series-data';
import { MAX_CAPITAL } from '@/views/containers/comparison/Charts/types';

export const DEFAULT_OPTIONS = {
  capital: CHART_STARTING_CAPITAL,
  startDate: moment().startOf('month').subtract(36, 'month').toDate(),
  endDate: moment().toDate(),
};

type HeaderOptions = {
  hideDateRange?: boolean;
  hideTargetRate?: boolean;
  hideSegmentedControl?: boolean;
  hideCapitalAmount?: boolean;
  showInlineTitle?: boolean;
};

type DefaultProps = {
  animate: boolean;
  height: Nullable<number>;
};

type Props = {
  user: User;
  localCurrency: LocalCurrency;
  items: ComparisonItem[];
  dark?: boolean;
  compact?: boolean;
  color?: string;
  detailedDisclaimer?: boolean;
  showItemNames?: boolean;
  options: GrowthChartOptions;
  headerOptions: HeaderOptions;
  chartPicker?: React.ReactNode;
  onOptionsChange: (options: GrowthChartOptions) => void;
  withLegend?: boolean;
  withAnnualizedROILegend?: boolean;
  expandedPortfoliosData?: Dictionary<{ funds: Dictionary<Fund>; stocks: Dictionary<Stock> }>;
  showBaseCurrencies?: boolean;
  shortHistory?: boolean;
  showShortHistoryDisclaimer?: boolean;
  customDisclaimer?: boolean;
  locale: Locale;
  appRegion: AppRegion;
  readOnly?: boolean;
  dateQuickRangeValues: TIME_PERIODS[];
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS;
} & Partial<DefaultProps>;

type State = {
  interval: ROLLING_INTERVALS;
  startDate: Nullable<Date>;
  endDate: Date;
  targetROI?: number;
  chartOptions: Highcharts.Options;
  rebalancePeriod: RebalanceFrequency;
  capital: number;
  useMonthly: boolean;
  dateQuickRange: Nullable<TIME_PERIODS>;
};

const getDefaultStartDate = (user: User, startDate?: Nullable<Date>): { startDate: Nullable<Date>; dateQuickRange: Nullable<TIME_PERIODS> } => {
  let date: Nullable<Date> = DEFAULT_OPTIONS.startDate;
  let dateQuickRange = null;

  if (startDate === null) {
    date = null;
  } else if (startDate !== undefined) {
    date = moment(startDate).toDate();
  } else if (user.preferences?.comparisonSettings?.hypotheticalReturnChartHorizon) {
    const months = periodToMonths(user.preferences.comparisonSettings.hypotheticalReturnChartHorizon as TIME_PERIODS);

    if (months === PERIODS_VALUE_INC || months === PERIODS_VALUE_CI) {
      date = null;
      dateQuickRange = user.preferences.comparisonSettings.hypotheticalReturnChartHorizon;
    }

    date = moment().startOf('month').subtract(months, 'month').toDate();
    dateQuickRange = user.preferences.comparisonSettings.hypotheticalReturnChartHorizon;
  }

  return {
    startDate: date || null,
    dateQuickRange: dateQuickRange as Nullable<TIME_PERIODS>,
  };
};

class GrowthChart extends Component<Props, State> {
  static defaultProps = {
    animate: true,
    height: null,
    options: {},
    onOptionsChange: () => {},
    showShortHistoryDisclaimer: true,
    locale: LOCALE.EN_CA,
  };

  private readonly REBALANCING_OPTIONS = [
    { label: formatMessage('Charts.Growth.RebalanceOptions.Daily'), value: REBALANCING_PERIODS.DAILY.value },
    { label: formatMessage('Charts.Growth.RebalanceOptions.Quarterly'), value: REBALANCING_PERIODS.QUARTERLY.value },
    { label: formatMessage('Charts.Growth.RebalanceOptions.Semiannually'), value: REBALANCING_PERIODS.SEMIANNUALLY.value },
    { label: formatMessage('Charts.Growth.RebalanceOptions.Yearly'), value: REBALANCING_PERIODS.YEARLY.value },
  ];

  readonly state: State = {
    capital: this.props.options.capital != null ? this.props.options.capital : DEFAULT_OPTIONS.capital,
    interval: ROLLING_INTERVALS.WEEKLY,
    ...getDefaultStartDate(this.props.user, this.props.options.startDate),
    endDate: this.props.options.endDate != null ? moment(this.props.options.endDate).toDate() : DEFAULT_OPTIONS.endDate,
    targetROI: this.props.options.targetROI,
    rebalancePeriod: this.props.options.rebalancePeriod != null ? this.props.options.rebalancePeriod : REBALANCING_PERIODS.DAILY,
    useMonthly: !!this.props.options.useMonthly,
    chartOptions: getGrowthChartOptions({
      height: this.props.height,
      locale: this.props.locale,
      animate: this.props.animate || true,
    }),
  };

  componentDidMount() {
    const { items, expandedPortfoliosData, shortHistory, appRegion } = this.props;
    const { endDate, capital = 0, startDate, useMonthly, chartOptions } = this.state;

    const portfolioHoldingItems = getExpandedPortfolioItems(items, appRegion, expandedPortfoliosData);
    const chartItems = mergeChartItems(items, portfolioHoldingItems);
    const earliestStartDate = startDate !== null ? this.getGraphStartDate(chartItems, startDate) : null;
    const interval = getRollingIntervalFromDates(earliestStartDate, endDate, items, useMonthly, shortHistory);
    const series = this.getSeriesData(chartItems, capital);

    this.setLanguage();
    const newOptions = { ...chartOptions, series };
    this.setState({ interval, startDate: earliestStartDate, chartOptions: newOptions });

    setTimeout(() => {
      dom.fireResizeEvent();
    }, 100);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { items, height, options, expandedPortfoliosData, shortHistory, appRegion } = this.props;
    const { chartOptions } = this.state;

    let { capital, targetROI, startDate, endDate, rebalancePeriod, useMonthly } = this.state;

    if (options && options.capital && prevProps.options.capital !== options.capital) {
      capital = options.capital;
    }

    if (options && options.rebalancePeriod && prevProps.options.rebalancePeriod !== options.rebalancePeriod) {
      rebalancePeriod = options.rebalancePeriod;
    }

    if (options && options.targetROI && prevProps.options.targetROI !== options.targetROI) {
      targetROI = options.targetROI;
    }

    if (options && options.startDate !== undefined && prevProps.options.startDate !== options.startDate) {
      startDate = options.startDate;
    }

    if (options && options.endDate && prevProps.options.endDate !== options.endDate) {
      endDate = options.endDate;
    }

    if (options !== undefined && options.useMonthly !== undefined && prevProps.options.useMonthly !== options.useMonthly) {
      useMonthly = options.useMonthly;
    }

    if (
      prevProps.items.length !== items.length ||
      (prevProps.items.length === items.length && !prevProps.items.every(item => items.some(i => i === item))) ||
      prevProps.height !== height ||
      (prevState.startDate && prevState.startDate.getTime()) !== (startDate && startDate.getTime()) ||
      prevState.endDate.getTime() !== endDate.getTime() ||
      prevState.targetROI !== targetROI ||
      prevState.capital !== capital ||
      prevState.rebalancePeriod !== rebalancePeriod ||
      prevProps.expandedPortfoliosData !== expandedPortfoliosData ||
      prevProps.shortHistory !== shortHistory ||
      prevState.useMonthly !== useMonthly ||
      prevState.rebalancePeriod !== rebalancePeriod
    ) {
      const portfolioHoldingItems = getExpandedPortfolioItems(items, appRegion, expandedPortfoliosData);
      const chartItems = mergeChartItems(items, portfolioHoldingItems);
      const interval = getRollingIntervalFromDates(startDate, endDate, items, useMonthly, shortHistory);
      const series = this.getSeriesData(chartItems, capital);
      const chart = {
        height,
        spacingLeft: 0,
        spacingRight: 0,
      };
      const newChartOptions = Object.assign(chartOptions, { series, chart });
      this.setState({ useMonthly, interval, targetROI, startDate, endDate, capital, chartOptions: newChartOptions });
    }
  }

  setLanguage = () => {
    const { locale } = this.props;
    const currentOptions = Highcharts.getOptions();
    Highcharts.setOptions({
      lang: { ...currentOptions.lang, ...LABEL_FORMATTERS.lang(locale) },
    });
  };

  getSeriesData = (data: ChartItem[], capital: number = DEFAULT_OPTIONS.capital) => {
    const { endDate, interval, rebalancePeriod, startDate, targetROI } = this.state;
    const { color, localCurrency, shortHistory, showBaseCurrencies } = this.props;
    const earliestStartDate = startDate !== null ? this.getGraphStartDate(data, startDate) : null;
    return getSeriesDataImpl(
      data,
      capital,
      showBaseCurrencies,
      shortHistory,
      localCurrency,
      startDate,
      earliestStartDate,
      endDate,
      interval,
      targetROI,
      color,
      rebalancePeriod,
    );
  };

  getGraphStartDate = (items: ChartItem[], startDate?: Nullable<Date>) => {
    const { showBaseCurrencies } = this.props;

    let earliestStartDate = startDate || moment().startOf('month').subtract(36, 'month').toDate();

    // If any of the items has less data we'll set the default range to 1Y
    for (const item of items) {
      const performances = showBaseCurrencies ? [item.performance, item.performanceBase] : [item.performance];

      for (const performance of performances) {
        const weeklyRolling = (performance && performance.rolling && performance.rolling.weekly) || [];
        if (!startDate && weeklyRolling.length && weeklyRolling[0][0] > earliestStartDate.getTime()) {
          earliestStartDate = moment().startOf('month').subtract(12, 'month').toDate();
        }
      }
    }

    return earliestStartDate;
  };

  onRebalanceChange = (value: any) => {
    const rebalancePeriod = REBALANCING_PERIODS[value.toUpperCase()];
    this.setState({ rebalancePeriod });
  };

  setOptions = <K extends keyof GrowthChartOptions>(options: Pick<GrowthChartOptions, K>) => {
    this.setState(
      prevState => ({ ...prevState, ...options }),
      () => {
        this.props.onOptionsChange(options);
      },
    );
  };

  changeDate = (startDate: Nullable<Date>, endDate: Date, isYTD?: boolean) => {
    // Subtract 1 day to not spill over next month
    const newEndDate = moment.min(moment(endDate).endOf('month').subtract(1, 'days'), moment()).toDate();

    this.setOptions({ startDate, endDate: newEndDate, isYTD: isYTD || false });
  };

  onDateRangeChange = (startDate: Nullable<Date>, endDate: Date, isYTD?: boolean) => {
    this.setState({ dateQuickRange: null });
    this.changeDate(startDate, endDate, isYTD);
  };

  onQuickDateChange = (dateQuickRange: TIME_PERIODS) => {
    this.setState({ dateQuickRange });

    const monthsRange = periodToMonths(dateQuickRange);

    // TODO this is a workaround for now, if we ever add months to the DateQuickRange we'll need to rethink this
    const isYTD = monthsRange !== PERIODS_VALUE_CI && monthsRange !== PERIODS_VALUE_INC && monthsRange < 12;

    const endDate = getMaxEndDate(this.props.monthEndDataOption);
    const startDate =
      monthsRange === PERIODS_VALUE_CI || monthsRange === PERIODS_VALUE_INC ? null : moment(endDate).startOf('month').subtract(monthsRange, 'months').toDate();

    this.changeDate(startDate, endDate, isYTD);
  };

  onChangeTargetROI = (value: string) => {
    const targetROI = value ? Number(value) : undefined;

    this.setOptions({ targetROI });
  };

  getRebalancingLabel = () => {
    const option = this.REBALANCING_OPTIONS.find(opt => opt.value === this.state.rebalancePeriod.value);
    return option ? option.label : '';
  };

  onCapitalChange = (value: string) => {
    const capital = value ? Math.min(Number(value), MAX_CAPITAL) : 0;
    this.setOptions({ capital });
  };

  onBlurCapitalChange = () => {
    const { capital } = this.state;

    if (capital === undefined) {
      this.setOptions({ capital: DEFAULT_OPTIONS.capital });
    }
  };

  onIntervalChange = (selected: boolean) => {
    this.setOptions({ useMonthly: selected });
  };

  handleRangeStartDate = (startDate: Nullable<Date>) => {
    if (startDate) return startDate;
    const { chartOptions } = this.state;
    if (!chartOptions.series || !Object.values(chartOptions.series).some(serie => serie?.data?.length > 0)) return null;
    const filteredSeries = chartOptions.series
      .map(serie => serie.data[0][0])
      .filter((item, pos, self) => self.indexOf(item) === pos)
      .sort((a, b) => a - b);
    return filteredSeries?.length ? new Date(filteredSeries[0]) : null;
  };

  render() {
    const {
      dark,
      readOnly,
      compact,
      items,
      detailedDisclaimer,
      showItemNames,
      withLegend,
      withAnnualizedROILegend,
      showBaseCurrencies,
      shortHistory,
      showShortHistoryDisclaimer,
      customDisclaimer,
      chartPicker,
      options,
      headerOptions,
      locale,
      dateQuickRangeValues,
    } = this.props;

    const { startDate, endDate, chartOptions, targetROI, interval } = this.state;

    const dateRange = startDate ? moment(endDate).diff(startDate, 'months') : null;
    const dateQuickRange = monthsToPeriodFromValues(dateRange, undefined, dateQuickRangeValues);

    const targetROIClasses = classNames('target-rate', 'd-flex', 'align-items-center', {
      print: !!targetROI,
    });

    const chartWarning = getGraphPerformanceDisclaimer(items, interval, startDate, endDate, shortHistory, options.useMonthly);
    const showFooterDisclaimer = chartOptions.series && Object.values(chartOptions.series).some(serie => serie.data.length > 0);
    const comparisonHasBenchmarks = items.some(item => item.type === COMPARISON_ITEM_TYPES.BENCHMARK);
    const chartHasData = chartOptions && chartOptions.series && chartOptions.series.length > 0;
    const { capital } = this.state;
    const formattedCapital = formatNumber(capital != null ? capital : DEFAULT_OPTIONS.capital);

    return (
      <div className={classNames('growth-chart chart', { dark, 'no-item': !items.length })}>
        {!headerOptions?.hideCapitalAmount && (
          <div className="title print">
            <FormattedMessage id="Charts.Growth.Title" />
            {'\u00a0'}
            <EditableInput
              readOnly={readOnly}
              compact
              type="number"
              dark={dark}
              value={capital}
              onBlur={this.onBlurCapitalChange}
              formattedValue={formatMessage('Charts.Growth.FormattedCurrency', { capital: formattedCapital })}
              onValueChange={this.onCapitalChange}
            />
          </div>
        )}
        {!compact && !!items.length && (
          <div className={classNames('chart-toolbar-container', { 'inline-toolbar-container': headerOptions?.showInlineTitle })}>
            {headerOptions?.showInlineTitle && <h1 className="growth-title">{formatMessage('Charts.Growth.AnnualizedRateOfReturn')}</h1>}

            {chartPicker}
            <div className="toolbar d-flex justify-content-end align-items-center">
              <div className={classNames('options-container d-flex align-items-center', { print: !!targetROI })}>
                {!headerOptions?.hideTargetRate && (
                  <div className={targetROIClasses}>
                    <label>
                      <FormattedMessage id="Charts.Growth.TargetROI" />
                    </label>

                    <Input
                      readOnly={readOnly}
                      className="target-roi"
                      dark={dark}
                      compact
                      type="number"
                      onValueChange={this.onChangeTargetROI}
                      value={targetROI}
                      invert={dark}
                    />

                    <label>
                      <span className="print-only">{targetROI}</span>%
                    </label>
                  </div>
                )}

                {!headerOptions?.hideSegmentedControl && (
                  <div className="no-print">
                    <DateQuickRangeOneWay
                      selected={dateQuickRange}
                      onChange={this.onQuickDateChange}
                      dark={dark}
                      values={dateQuickRangeValues}
                      darkerBackground={dark}
                    />
                  </div>
                )}
              </div>

              <div className="toolbar-date-warning">
                {!headerOptions?.hideDateRange && (
                  <DateRangeSelect
                    className="print"
                    startDate={this.handleRangeStartDate(startDate)}
                    endDate={endDate}
                    onChange={this.onDateRangeChange}
                    maxDate={getMaxEndDate(this.props.monthEndDataOption)}
                    dark={dark}
                    locale={locale}
                    readOnly={readOnly}
                  />
                )}

                {chartWarning && (
                  <div className="toolbar-icons d-flex align-items-center">
                    <DisclaimerTooltip className="toolbar-disclaimer" disclaimer={chartWarning} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {withLegend && <GrowthLegend chartOptions={chartOptions} sort={false} showBaseCurrencies={showBaseCurrencies} />}
        {withAnnualizedROILegend && (
          <GrowthAnnualizedROILegend targetName={options.targetName} targetROI={options.targetROI} items={items} dateQuickRange={this.state.dateQuickRange} />
        )}

        <div className="graph-container">
          {chartHasData && !!items.length && (
            <HighchartsReact
              className="highchart"
              containerProps={{ className: 'highcharts-react-container' }}
              highcharts={Highcharts}
              options={chartOptions}
              constructorType="stockChart"
            />
          )}

          {!chartHasData && items.length > 0 && (
            <div className={classNames('empty-chart flex-fill', { 'is-light': !dark })}>
              <FormattedMessage id="Charts.EmptyPeriod" />
            </div>
          )}

          {!items.length && <GenericEmptyState dark={dark} header={formatMessage('Charts.NoItem.Title')} body={formatMessage('Charts.NoItem.Body')} />}
        </div>

        {showFooterDisclaimer && chartOptions.series && (
          <div className="chart-disclaimer">
            <HypotheticalReturnDisclaimer
              shorthand={!detailedDisclaimer}
              showItemNames={showItemNames}
              items={items}
              series={chartOptions.series.reduce((acc, serie) => acc.concat(serie.data), [])}
              interval={interval}
            />

            {comparisonHasBenchmarks && <BenchmarksDisclaimer items={items} />}

            {shortHistory && showShortHistoryDisclaimer && (
              <ShortHistoryPortfoliosDisclaimer items={items} startDate={options.startDate} endDate={options.endDate} />
            )}

            {customDisclaimer && (
              // Custom disclaimers on charts are only used in V1 proposals, so it's safe to target
              // only disclaimers tagged with PROPOSAL. Should this assumption change, then this logic
              // should be revisited.
              <InstrumentCustomDisclaimer items={items} disclaimerTags={[DISCLAIMER_TAG.PROPOSAL]} />
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withErrorBoundary(GrowthChart);
